import clsx from "clsx";

import type { CalendarActions, CalendarDay } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { weekdayNames } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { DayHeader } from "~/modules/dashboard/components/TimeTrackingCalendar/DayHeader.tsx";
import { HolidayAndAbsenceBars } from "~/modules/dashboard/components/TimeTrackingCalendar/HolidayAndAbsenceBars.tsx";
import { TimeTrackingPopoverBar } from "~/modules/dashboard/components/TimeTrackingCalendar/TimeTrackingPopoverBar.tsx";
import { AddTimeTrackingButton } from "~/modules/timeTracking/components/components/AddTimeTrackingButton.tsx";

type Props = CalendarActions & {
	days: CalendarDay[];
	openDay: (date: Date) => void;
};

/**
 * The month from `md`: seven columns, each day with its holiday, its absences, its trackings and its "+".
 * The holiday is header text, not a bar, because a bar takes a full row of a small cell.
 * A weekend is dimmed like a neighbor month. Its holiday name is one step darker (see `DayHeader`), so that it stays readable on the dimmed background.
 * A tracking shows its title only, so that the title has room in the narrow cell. The day view and the popover show the hours.
 */
export function MonthGrid(props: Props) {
	const { absenceActions, days, openDay, canAddTrackingOn, canAddAbsence, showDisabledAddTracking, openCreate } = props;

	return (
		<div className="grid grid-cols-7 gap-px bg-gray-200 text-sm">
			{weekdayNames.map((weekdayName) => (
				<div key={weekdayName}
					 className="bg-white py-2 text-center text-xs font-semibold text-gray-500">{weekdayName}</div>
			))}
			{days.map((day) => {
				const canAddEntry = day.isInMonth && (canAddAbsence || canAddTrackingOn(day.date));
				return (
					<div key={day.date.toISOString()}
						 className={clsx("group/cell flex min-h-24 min-w-0 flex-col gap-1 p-1", day.isInMonth && !day.isWeekend ? "bg-white" : "bg-gray-50")}>
						<DayHeader day={day}
								   openDay={openDay} />
						{0 < day.absences.length && (
							<div className={clsx("flex flex-col gap-1", !day.isInMonth && "opacity-50")}>
								<HolidayAndAbsenceBars day={day}
														absenceActions={absenceActions}
														size="sm"
														hideHoliday />
							</div>
						)}
						{day.timeTrackings.map((timeTracking) => (
							<TimeTrackingPopoverBar key={timeTracking.id}
													timeTracking={timeTracking}
													{...props} />
						))}
						{day.isInMonth && (canAddEntry || showDisabledAddTracking) && (
							<AddTimeTrackingButton date={day.date}
												   canAddTracking={canAddEntry}
												   openCreate={openCreate}
												   // focus-within: a keyboard user cannot hover, so the "+" shows when the focus is in the cell.
												   className="opacity-0 group-focus-within/cell:opacity-100 group-hover/cell:opacity-100" />
						)}
					</div>
				);
			})}
		</div>
	);
}
